import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop } from "../../utils";
import { saveNodeType } from "../utils";
import { getXValue } from "../../utils/ChartDataUtil";

import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

class EachFibonacciTimeZones extends Component {
  constructor(props) {
    super(props);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleHover = this.handleHover.bind(this);

    this.isHover = this.isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, x2 } = this.props;
    this.dragStart = { x1, x2 };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, x2 } = this.dragStart;

    const { xScale, xAccessor, fullData } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const px1 = xScale(x1);
    const px2 = xScale(x2);

    const dx = startPos[0] - mouseXY[0];

    const newX1 = getXValue(xScale, xAccessor, [px1 - dx, 0], fullData);
    const newX2 = getXValue(xScale, xAccessor, [px2 - dx, 0], fullData);

    onDrag(index, {
      x1: newX1,
      x2: newX2,
    });
  }

  handlePoint1Drag(moreProps) {
    const { index, onDrag, x2 } = this.props;
    const { xScale, xAccessor, fullData, mouseXY } = moreProps;

    const newX1 = getXValue(xScale, xAccessor, mouseXY, fullData);

    onDrag(index, {
      x1: newX1,
      x2,
    });
  }

  handlePoint2Drag(moreProps) {
    const { index, onDrag, x1 } = this.props;
    const { xScale, xAccessor, fullData, mouseXY } = moreProps;

    const newX2 = getXValue(xScale, xAccessor, mouseXY, fullData);

    onDrag(index, {
      x1,
      x2: newX2,
    });
  }

  helper(props, moreProps) {
    const { x1, x2 } = props;
    const { xScale, chartConfig } = moreProps;
    const { yScale } = chartConfig;

    // Convert time coordinates to pixel coordinates
    const px1 = xScale(x1);
    const px2 = xScale(x2);

    // Calculate base interval in ticks
    const baseInterval = x2 - x1;

    // Generate Fibonacci sequence (same algorithm as ChartIQ)
    // Start with [1, 0] and generate: 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144...
    const fibs = [1, 0];
    const fibPositions = [];
    const maxLines = 15; // Reasonable limit to avoid performance issues

    // Always include the first line at position 0
    fibPositions.push({
      fibNumber: 0,
      x: x1,
      px: px1,
    });

    // Generate Fibonacci sequence and calculate positions
    let count = 0;
    while (count < maxLines) {
      const nextFib = fibs[0] + fibs[1];
      fibs.unshift(nextFib);

      const xPos = x1 + nextFib * baseInterval;
      const pxPos = xScale(xPos);

      // Only include lines that are reasonably visible
      // (allow some overflow for better UX when dragging)
      fibPositions.push({
        fibNumber: nextFib,
        x: xPos,
        px: pxPos,
      });

      count++;

      // Stop if we've gone way off screen (in the direction of p2)
      if (baseInterval > 0 && pxPos > chartConfig.width + 200) break;
      if (baseInterval < 0 && pxPos < -200) break;
    }

    // Get chart bounds for vertical lines
    const top = chartConfig.yScale.range()[0];
    const bottom = chartConfig.yScale.range()[1];

    return {
      px1,
      px2,
      baseInterval,
      fibPositions,
      top: Math.min(top, bottom),
      bottom: Math.max(top, bottom),
    };
  }

  isHover(moreProps) {
    const { mouseXY, chartConfig } = moreProps;
    const [mx, my] = mouseXY;

    const { px1, px2, fibPositions, top, bottom } = this.helper(
      this.props,
      moreProps,
    );

    if (!fibPositions || fibPositions.length === 0) return false;

    const tolerance = 10;
    const halfChartHeight = (bottom - top) / 2;
    const midY = top + halfChartHeight;

    // Check distance to anchor points (at middle of chart)
    const distToP1 = Math.sqrt(Math.pow(mx - px1, 2) + Math.pow(my - midY, 2));
    const distToP2 = Math.sqrt(Math.pow(mx - px2, 2) + Math.pow(my - midY, 2));

    if (distToP1 < tolerance || distToP2 < tolerance) {
      return true;
    }

    // Check if mouse is near any vertical Fibonacci line
    for (const pos of fibPositions) {
      if (Math.abs(mx - pos.px) < tolerance && my >= top && my <= bottom) {
        return true;
      }
    }

    return false;
  }

  renderSVG(moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;
    const { chartConfig } = moreProps;

    const stroke = appearance.stroke?.color || "#FF9800";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined
        ? appearance.stroke.opacity
        : 0.5;
    const strokeDasharray =
      appearance.stroke?.dash === "Solid" ? "none" : "5,5";

    const fillColor = appearance.fill?.color || stroke;
    const fillOpacity =
      appearance.fill?.opacity !== undefined ? appearance.fill.opacity : 0.05;

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontColor = appearance.font?.color || stroke;

    const { px1, px2, baseInterval, fibPositions, top, bottom } = this.helper(
      this.props,
      moreProps,
    );

    // Don't render if no valid interval
    if (baseInterval === 0 || !fibPositions || fibPositions.length === 0) {
      return null;
    }

    const halfChartHeight = (bottom - top) / 2;
    const midY = top + halfChartHeight;
    const labelY = bottom - 5; // Position labels near bottom

    return (
      <g>
        {/* Fill areas between consecutive vertical lines */}
        {fibPositions.map((pos, idx) => {
          if (idx === 0) return null; // Skip first position (no previous line to fill from)
          const prevPos = fibPositions[idx - 1];
          return (
            <rect
              key={`fill-${idx}`}
              x={Math.min(prevPos.px, pos.px)}
              y={top}
              width={Math.abs(pos.px - prevPos.px)}
              height={bottom - top}
              fill={fillColor}
              opacity={fillOpacity}
            />
          );
        })}

        {/* Vertical Fibonacci lines */}
        {fibPositions.map((pos, idx) => (
          <g key={`line-${idx}`}>
            <line
              x1={pos.px}
              y1={top}
              x2={pos.px}
              y2={bottom}
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeOpacity={strokeOpacity}
              strokeDasharray={strokeDasharray}
            />
            {/* Fibonacci number labels at bottom */}
            <text
              x={pos.px}
              y={labelY}
              textAnchor="middle"
              fontSize={fontSize}
              fontFamily={fontFamily}
              fill={fontColor}
              opacity={strokeOpacity}
            >
              {baseInterval > 0 ? pos.fibNumber : -pos.fibNumber}
            </text>
          </g>
        ))}

        {/* Trend line connecting p0 to p1 */}
        <line
          x1={px1}
          y1={midY}
          x2={px2}
          y2={midY}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity * 0.5}
          strokeDasharray="3,3"
        />

        {/* Drag handles at middle of chart */}
        {(selected || hover) && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={px1}
              cy={midY}
              r={6}
              fill={stroke}
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={this.props.onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point2")}
              show={true}
              cx={px2}
              cy={midY}
              r={6}
              fill={stroke}
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint2Drag}
              onDragComplete={this.props.onDragComplete}
            />
          </g>
        )}
      </g>
    );
  }

  drawOnCanvas(ctx, moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#FF9800";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined
        ? appearance.stroke.opacity
        : 0.5;
    const strokeDasharray = appearance.stroke?.dash === "Solid" ? [] : [5, 5];

    const fillColor = appearance.fill?.color || stroke;
    const fillOpacity =
      appearance.fill?.opacity !== undefined ? appearance.fill.opacity : 0.05;

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontColor = appearance.font?.color || stroke;

    const { px1, px2, baseInterval, fibPositions, top, bottom } = this.helper(
      this.props,
      moreProps,
    );

    // Don't render if no valid interval
    if (baseInterval === 0 || !fibPositions || fibPositions.length === 0) {
      return;
    }

    const halfChartHeight = (bottom - top) / 2;
    const midY = top + halfChartHeight;
    const labelY = bottom - 5;

    // Draw fill areas between consecutive vertical lines
    ctx.fillStyle = fillColor;
    ctx.globalAlpha = fillOpacity;
    fibPositions.forEach((pos, idx) => {
      if (idx === 0) return;
      const prevPos = fibPositions[idx - 1];
      ctx.fillRect(
        Math.min(prevPos.px, pos.px),
        top,
        Math.abs(pos.px - prevPos.px),
        bottom - top,
      );
    });

    // Draw vertical Fibonacci lines
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.globalAlpha = strokeOpacity;

    if (strokeDasharray.length > 0) {
      ctx.setLineDash(strokeDasharray);
    } else {
      ctx.setLineDash([]);
    }

    fibPositions.forEach((pos) => {
      ctx.beginPath();
      ctx.moveTo(pos.px, top);
      ctx.lineTo(pos.px, bottom);
      ctx.stroke();
    });

    // Draw Fibonacci number labels
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = fontColor;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.globalAlpha = strokeOpacity;

    fibPositions.forEach((pos) => {
      const label = baseInterval > 0 ? pos.fibNumber : -pos.fibNumber;
      ctx.fillText(label.toString(), pos.px, labelY);
    });

    // Draw trend line
    ctx.setLineDash([3, 3]);
    ctx.globalAlpha = strokeOpacity * 0.5;
    ctx.beginPath();
    ctx.moveTo(px1, midY);
    ctx.lineTo(px2, midY);
    ctx.stroke();

    // Draw drag handles if selected or hovered
    if (selected || hover) {
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      // Draw point 1
      ctx.fillStyle = stroke;
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px1, midY, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Draw point 2
      ctx.beginPath();
      ctx.arc(px2, midY, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    // Reset context
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  }

  render() {
    const { x1, x2, hoverText, selected } = this.props;
    const { hover } = this.state;

    if (x2 === null || x2 === undefined) {
      return null;
    }

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    return (
      <g>
        <GenericChartComponent
          isHover={this.isHover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={this.props.onDragComplete}
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          interactiveCursorClass="react-stockcharts-move-cursor"
          selected={selected}
          enableDragOnHover={true}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

EachFibonacciTimeZones.propTypes = {
  index: PropTypes.number,

  x1: PropTypes.number.isRequired,
  x2: PropTypes.number,

  appearance: PropTypes.object.isRequired,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

EachFibonacciTimeZones.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  selected: false,

  hoverText: {
    enable: false,
  },
};

export default EachFibonacciTimeZones;
